import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative overflow-hidden rounded-xl cursor-pointer"
      >
        <div className="relative w-full h-[400px] md:h-[480px]">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-500 text-background-50 dark:text-foreground-950 rounded-full mb-3">
            {article.categoryName}
          </span>
          <h2 className="text-xl md:text-2xl font-bold text-white mb-2 line-clamp-2 font-heading">
            {article.title}
          </h2>
          <p className="text-sm text-white/70 line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-white/60">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(article.viewCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-message-2-line"></i>
              {formatCount(article.commentCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span>·</span>
            <span className="flex items-center gap-0.5">
              <i className="ri-eye-line"></i>
              {formatCount(article.viewCount)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-background-300 transition-all cursor-pointer"
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 text-xs font-medium bg-background-50/90 text-foreground-800 rounded-full">
            {article.categoryName}
          </span>
        </div>
        {article.isHot && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-0.5 text-xs font-bold bg-accent-500 text-background-50 dark:text-foreground-950 rounded-full">
              热门
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors font-heading">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {article.publishDate}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(article.viewCount)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-message-2-line"></i>
              {formatCount(article.commentCount)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function formatCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }
  return count.toString();
}